import React, { useState, useEffect, useRef, useCallback } from "react";
import env from "@beam-australia/react-env";

import { BlockUI } from "primereact/blockui";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";

import { useTranslation } from "react-i18next";

import { useDebounce } from "../../../../hooks/useDebounce";
import useCRUDService from "../../../../hooks/useCRUDService";
import useApi from "../../../../hooks/useApi";
import WithholdingsForm from "./withholdingsForm";
import Layout from "../../../../layout/layout";
import Header from "../../../../layout/header";
import useSkeleton from "../../../../hooks/useSkeleton";

import helperFormats from "../../../../utils/helperFormats";

import { atom, useAtom } from "jotai";
import { loadingState } from "../../../../states/loadingState";

const Withholdings = () => {
  const [isLoading, setIsLoading] = useAtom(loadingState);

  const s = useSkeleton("commons");
  const api = useApi();
  const { t } = useTranslation();
  const CRUDService = useCRUDService("withholdings");

  const [entities, setEntities] = useState(null);
  const [formDialog, setFormDialog] = useState(false);

  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(100);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [globalFilter, setGlobalFilter] = useState("");

  const [totalRecords, setTotalRecords] = useState(0);

  const searchInput = useRef(null);

  // Inicializamos el debounce para la búsqueda
  const debouncedSearchTerm = useDebounce(globalFilter, 2000);

  const toast = useRef(null);
  const dt = useRef(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const columns = JSON.stringify([
        {
          name: "year",
          type: "number",
        },
        {
          name: "month",
          type: "number",
        },
        {
          name: "createdDate",
          type: "date",
        },
        {
          name: "sentDate",
          type: "date",
        },
        {
          name: "confirmedDate",
          type: "date",
        },
        {
          name: "conciliationDate",
          type: "date",
        },
      ]);

      const response = await CRUDService.getEntities(
        columns,
        first,
        rows,
        sortField ?? "createdDate",
        sortOrder >= 0 ? "ASC" : "DESC",
        debouncedSearchTerm
      );

      setTotalRecords(response.data.totalRows);
      setEntities(response.data.rows);
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: t("errors.general"),
        life: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  }, [first, rows, sortField, sortOrder, debouncedSearchTerm, CRUDService]);

  useEffect(() => {
    fetchData();
  }, [first, rows, sortField, sortOrder, debouncedSearchTerm]);

  const openFormDialog = async (formMode, selectedWithholdings) => {
    setFormSettings((prev) => ({
      ...prev,
      formMode: formMode,
      entityId: selectedWithholdings?.id,
      hideDialog: hideDialog,
      toast: toast,
      CRUDService: CRUDService,
    }));

    setFormDialog(true);
  };

  const hideDialog = (action) => {
    if (action !== "CANCEL") {
      setFormDialog(false);
      // Restablece todos los filtros a sus valores predeterminados
      setFirst(0);
      setRows(100);
      setSortField(null);
      setSortOrder(null);
      setGlobalFilter("");
      // Restablece el valor del campo de entrada
      if (searchInput.current) {
        searchInput.current.value = "";
      }
      // Luego, realiza una nueva búsqueda con los filtros restablecidos
      fetchData();
    }

    setFormDialog(false);
  };

  const exportCSV = () => {
    dt.current.exportCSV();
  };

  const [formSettings, setFormSettings] = useState({
    entityId: -1,
    formMode: "",
    hideDialog: () => { },
    toast: toast,
    CRUDService: CRUDService,
  });

  const generateWithholdings = async () => {
    setIsLoading(true);

    try {
      const responseActiveToggle = await api.post(
        `${env("API_URL")}/withholdings/generate`
      );

      toast.current.show({
        severity: "success",
        summary: t("forms.messages.titles.success"),
        detail: t("forms.messages.operation.success"),
        life: 3000,
      });
      fetchData();
    } catch (error) {
      let severity = "error";
      let summary = t("forms.messages.titles.error");
      let detail = t("forms.messages.operation.error");
      if (error.response.data == "NO_INSTALLMENTS") {
        severity = "warn";
        summary = "No se generaron retenciones.";
        detail = `El próximo cuota se encuentra fuera del rango de tiempo requerido`;
      }
      toast.current.show({
        severity,
        summary,
        detail: detail,
        life: 3000,
      });
      throw new Error();
    } finally {
      setIsLoading(false);
    }
  };

  const actions = (
    <div className="flex justify-content-end">
      <Button
        label={t("entities.withholdings.generate")}
        icon="pi pi-bolt"
        className="p-button-secondary"
        onClick={generateWithholdings}
      />
    </div>
  );

  const actionBodyTemplate = (rowData) => {
    return (
      <div className="flex justify-content-center">
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-success"
          onClick={() => openFormDialog("UPDATE", rowData)}
        />
      </div>
    );
  };

  const header = (
    <div className="table-header">
      <span className="p-input-icon-left">
        <i className="pi pi-search" />
        <InputText
          ref={searchInput}
          type="search"
          onInput={(e) => setGlobalFilter(e.target.value)}
          placeholder={`${t("DataTable.Search")}...`}
        />
      </span>
      <Button
        outlined
        label={t("DataTable.Export")}
        icon="pi pi-upload"
        className="p-button-secondary"
        onClick={exportCSV}
        style={{
          marginLeft: "20px",
        }}
      />
    </div>
  );

  const getDialogTitle = () => {
    let action = "Add";

    if (formSettings.formMode == "DELETE") {
      action = "Delete";
    } else if (formSettings.formMode == "UPDATE") {
      action = "Edit";
    }

    return `${t(action)} ${t("entities.withholdings.singular")}`;
  };

  return (
    <Layout
      header={
        <Header
          icon={<i className="pi pi-percentage"></i>}
          title={t("entities.withholdings.plural")}
          actions={actions}
        />
      }
    >
      <Toast ref={toast} />
      <div className={s.get("dataTableContainer")}>
        <DataTable
          onPage={(e) => {
            setFirst(e.first);
            setRows(e.rows);
          }}
          onSort={(e) => {
            setSortField(e.sortField);
            setSortOrder(e.sortOrder);
          }}
          sortField={sortField}
          sortOrder={sortOrder}
          ref={dt}
          value={entities}
          lazy
          paginator
          first={first}
          rows={rows}
          totalRecords={totalRecords}
          columnResizeMode="fit"
          showGridlines
          dataKey="id"
          rowsPerPageOptions={[10, 25, 100, 1000]}
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          currentPageReportTemplate={`${t(
            "DataTable.currentPageReportTemplate"
          )} ${t("Withholdings")}`}
          globalFilter={globalFilter}
          header={header}
          className="flex flex-column w-full"
        >
          <Column
            field="year"
            header={t("entities.withholdings.year")}
            bodyStyle={{ textAlign: "center" }}
            sortable
          ></Column>
          <Column
            field="month"
            bodyStyle={{ textAlign: "center" }}
            header={t("entities.withholdings.month")}
            sortable
          ></Column>
          <Column
            field="createdDate"
            bodyStyle={{ textAlign: "center" }}
            header={t("entities.withholdings.createdDate")}
            body={(rowData) =>
              helperFormats.formatDate(new Date(rowData.createdDate))
            }
            sortable
          ></Column>
          <Column
            field="sentDate"
            header={t("entities.withholdings.sentDate")}
            bodyStyle={{ textAlign: "center" }}
            body={(rowData) =>
              helperFormats.isDate(rowData.sentDate)
                ? helperFormats.formatDate(new Date(rowData.sentDate))
                : "No enviada"
            }
            sortable
          ></Column>
          <Column
            field="confirmedDate"
            header={t("entities.withholdings.confirmedDate")}
            bodyStyle={{ textAlign: "center" }}
            body={(rowData) =>
              helperFormats.isDate(rowData.confirmedDate)
                ? helperFormats.formatDate(new Date(rowData.confirmedDate))
                : "No confirmada"
            }
            sortable
          ></Column>
          <Column
            field="conciliationDate"
            header={t("entities.withholdings.conciliationDate")}
            bodyStyle={{ textAlign: "center" }}
            body={(rowData) =>
              helperFormats.isDate(rowData.conciliationDate)
                ? helperFormats.formatDate(new Date(rowData.conciliationDate))
                : "No conciliada"
            }
            sortable
          ></Column>

          <Column
            body={actionBodyTemplate}
            exportable={false}
            style={{ width: "8rem", textAlign: "center" }}
          ></Column>
        </DataTable>
        <React.Fragment>
          <Dialog
            visible={formDialog}
            header={getDialogTitle()}
            modal
            className="flex w-9"
            onHide={hideDialog}
          >
            <WithholdingsForm settings={formSettings} />
          </Dialog>
        </React.Fragment>
      </div>
    </Layout>
  );
};

export default Withholdings;
